<template>
    <Dialog v-model:visible="visibleLocal" modal :dismissableMask="dialogState === 'idle'" :pt="dialogPt"
        @hide="onHide">
        <template #container="{ closeCallback }">
            <div class="flex flex-col gap-4 w-full">
                <!-- Success Message Card -->
                <Card v-if="dialogState === 'success'" class="max-w-lg mx-auto">
                    <template #content>
                        <div class="flex flex-col items-center justify-center gap-2 py-3">
                            <div class="flex items-center gap-2">
                                <i class="pi pi-check-circle text-green-500 text-2xl"></i>
                                <span>{{ t('billing.planSwitch.success') }}</span>
                            </div>
                        </div>
                    </template>
                </Card>

                <!-- Progress Indicator Card -->
                <Card v-else-if="isProgressState" class="max-w-lg mx-auto">
                    <template #content>
                        <div class="flex flex-col items-center justify-center gap-2 py-3">
                            <div class="flex items-center gap-2">
                                <ProgressSpinner style="width:24px;height:24px" strokeWidth="4" />
                                <span>{{ t(progressMessageKey!) }}</span>
                            </div>
                            <span class="text-xs text-color-secondary">{{ t('shared.loading_may_take_time') }}</span>
                        </div>
                    </template>
                </Card>

                <!-- Error Message Card -->
                <Card v-else-if="dialogState === 'error'" class="max-w-lg mx-auto">
                    <template #content>
                        <InlineMessage severity="error" :closable="false">
                            {{ errorMessage }}
                        </InlineMessage>
                    </template>
                </Card>

                <!-- Loading Pricing Plans -->
                <div v-else-if="isLoadingPricingPlans" class="flex items-center justify-center gap-2 py-4">
                    <ProgressSpinner style="width:24px;height:24px" strokeWidth="4" />
                    <span>{{ t('setup.pricing.loading') }}</span>
                </div>

                <!-- Pricing Plans Error -->
                <InlineMessage v-else-if="pricingPlansError" severity="error" :closable="false">
                    {{ t('setup.pricing.load-error') }}
                </InlineMessage>

                <!-- No Pricing Plans -->
                <div v-else-if="!pricingPlans">
                    <p>{{ t('setup.pricing.no-active') }}</p>
                </div>

                <!-- Pricing Plans (idle state) -->
                <div v-else class="relative">
                    <div class="flex flex-row gap-4">
                        <PricingPlans :plans="pricingPlans" :visible-plans="visiblePlansConfig"
                            :current-price-lookup-key="currentPriceLookupKey" @choose="handlePlanChoose" />
                        <Button icon="pi pi-times" rounded severity="danger" @click="closeCallback"
                            :aria-label="t('shared.close')" />
                    </div>
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Card from 'primevue/card'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import InlineMessage from 'primevue/inlinemessage'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useTenantsStore } from '@/stores/tenants'
import { useBillingStore } from '@/stores/billing'
import { usePlanVisibility } from '@/composables/planVisibility'
import { TenantPlan } from '@/models/Tenant'
import { PlanSwitchRequestStatus } from '@/models/PlanSwitchRequest'
import { CheckoutSessionRequestStatus } from '@/models/Billing'
import PricingPlans from '@/components/settings/billing/PricingPlans.vue'
import { useSelfServeGate } from '@/composables/useSelfServeGate'
import { logger } from '@/utils/logger'

const { shouldContactSalesForPlanSwitch, openContactSales } = useSelfServeGate()

// -----------------------------------------------------------------------------
// Dialog State Machine
// -----------------------------------------------------------------------------
type DialogState =
    | 'idle'           // Showing pricing plans
    | 'submitting'     // Request being created
    | 'processing'     // Waiting for backend to process plan switch
    | 'redirecting'    // About to redirect to Stripe
    | 'success'        // Plan switch completed (no payment needed)
    | 'error'          // Something failed

const props = defineProps<{
    visible: boolean
}>()

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()

const { t } = useI18n()
const tenantsStore = useTenantsStore()
const billingStore = useBillingStore()

const { pricingPlans, isLoadingPricingPlans, pricingPlansError } = storeToRefs(billingStore)

// -----------------------------------------------------------------------------
// Local State
// -----------------------------------------------------------------------------
const visibleLocal = ref(props.visible)
const dialogState = ref<DialogState>('idle')
const errorMessage = ref<string | null>(null)

// Sync visibility with props
watch(() => props.visible, (v) => (visibleLocal.value = v))
watch(visibleLocal, (v) => emit('update:visible', v))

// -----------------------------------------------------------------------------
// Computed
// -----------------------------------------------------------------------------
const currentPriceLookupKey = computed(() => {
    const items = tenantsStore.subscriptions?.items
    if (!items) return null
    for (const item of items) {
        const key = item.price?.lookup_key
        if (key && !key.startsWith('topup_')) return key
    }
    return null
})

const { planSwitchPlans: visiblePlansConfig } = usePlanVisibility()

const isProgressState = computed(() =>
    dialogState.value === 'submitting' ||
    dialogState.value === 'processing' ||
    dialogState.value === 'redirecting'
)

const progressMessageKey = computed(() => {
    switch (dialogState.value) {
        case 'submitting':
            return 'billing.planSwitch.inProgress.creatingCheckoutSessionRequest'
        case 'processing':
            return 'billing.planSwitch.inProgress.creatingCheckoutSession'
        case 'redirecting':
            return 'billing.planSwitch.inProgress.redirectingToCheckout'
        default:
            return null
    }
})

const dialogPt = computed(() => ({
    root: {
        class: 'plan-switch-transparent',
        style: { background: 'transparent', border: 'none', boxShadow: 'none' }
    },
    mask: {
        style: { background: 'transparent', backdropFilter: 'none' }
    }
}))

// -----------------------------------------------------------------------------
// State Transition Helper
// -----------------------------------------------------------------------------
function setDialogState(newState: DialogState, trigger: string) {
    const oldState = dialogState.value
    if (oldState === newState) return
    logger.debug('[PlanSwitchDialog] State transition', { from: oldState, to: newState, trigger })
    dialogState.value = newState
    if (newState === 'success') {
        const tenantId = tenantsStore.currentTenant?.id
        if (tenantId) {
            setTimeout(async () => {
                await Promise.all([
                    billingStore.fetchCreditBalance(tenantId),
                    tenantsStore.loadSubscription(),
                ])
            }, 3000)
        }
    }
}

// -----------------------------------------------------------------------------
// Store Watcher - Single source of truth for state transitions
// -----------------------------------------------------------------------------
watch(
    () => ({
        planSwitch: billingStore.planSwitchRequest,
        checkoutReq: billingStore.checkoutSessionRequest,
        checkoutSession: billingStore.checkoutSession,
    }),
    (current, previous) => {
        // Only process when dialog is visible and we're in an active flow
        if (!visibleLocal.value) return
        if (dialogState.value === 'idle' || dialogState.value === 'success') return

        const { planSwitch, checkoutReq, checkoutSession } = current

        logger.debug('[PlanSwitchDialog] Store watcher fired', {
            dialogState: dialogState.value,
            planSwitchStatus: planSwitch?.status,
            prevPlanSwitchStatus: previous?.planSwitch?.status,
            hasPaymentUrl: !!planSwitch?.payment_url,
            hasCheckoutSessionReq: !!planSwitch?.checkout_session_request,
        })

        // --- Check for errors first ---
        if (planSwitch?.status === PlanSwitchRequestStatus.FAILED) {
            errorMessage.value = planSwitch.error || t('billing.planSwitch.error')
            setDialogState('error', 'planSwitchRequest.FAILED')
            return
        }

        if (checkoutReq?.status === CheckoutSessionRequestStatus.FAILED) {
            errorMessage.value = checkoutReq.error || t('billing.planSwitch.error')
            setDialogState('error', 'checkoutSessionRequest.FAILED')
            return
        }

        if (checkoutSession?.error?.message) {
            errorMessage.value = checkoutSession.error.message
            setDialogState('error', 'checkoutSession.error')
            return
        }

        // --- Check for success (no payment needed) ---
        // Case 1: planSwitch is still available with DONE status
        if (planSwitch?.status === PlanSwitchRequestStatus.DONE &&
            !planSwitch.payment_url &&
            !planSwitch.checkout_session_request) {
            setDialogState('success', 'planSwitch.DONE (no payment)')
            setTimeout(() => { visibleLocal.value = false }, 1500)
            return
        }

        // Case 2: planSwitch was cleared by the store after DONE (no payment)
        // The store calls stopPlanSwitchSubscription() which clears the snapshot
        // before we can react, so we detect the transition from DONE to null
        if (!planSwitch &&
            previous?.planSwitch?.status === PlanSwitchRequestStatus.DONE &&
            !previous.planSwitch.payment_url &&
            !previous.planSwitch.checkout_session_request) {
            setDialogState('success', 'planSwitch cleared after DONE (no payment)')
            setTimeout(() => { visibleLocal.value = false }, 1500)
            return
        }

        // Case 3: planSwitch was cleared but we're in a progress state
        // This means the plan switch completed without needing checkout
        if (!planSwitch && !checkoutReq && !checkoutSession &&
            (dialogState.value === 'submitting' || dialogState.value === 'processing')) {
            setDialogState('success', 'all billing state cleared while in progress')
            setTimeout(() => { visibleLocal.value = false }, 1500)
            return
        }

        // --- Check for redirects ---
        // Direct payment URL (Stripe hosted invoice)
        if (planSwitch?.status === PlanSwitchRequestStatus.DONE && planSwitch.payment_url) {
            setDialogState('redirecting', 'planSwitch.payment_url')
            logger.debug('[PlanSwitchDialog] Redirecting to payment URL', { url: planSwitch.payment_url })
            window.location.replace(planSwitch.payment_url)
            return
        }

        // Checkout session URL ready
        if (checkoutSession?.url) {
            setDialogState('redirecting', 'checkoutSession.url')
            logger.debug('[PlanSwitchDialog] Redirecting to checkout', { url: checkoutSession.url })
            window.location.replace(checkoutSession.url)
            return
        }

        // --- Update progress states ---
        if (planSwitch?.status === PlanSwitchRequestStatus.DONE && planSwitch.checkout_session_request) {
            // Plan switch done, now waiting for checkout session
            if (checkoutReq?.status === CheckoutSessionRequestStatus.DONE && !checkoutSession) {
                setDialogState('redirecting', 'checkoutSession pending')
            } else if (checkoutReq) {
                setDialogState('processing', 'checkoutSessionRequest exists')
            } else {
                setDialogState('processing', 'waiting for checkoutSessionRequest')
            }
            return
        }

        if (planSwitch && planSwitch.status !== PlanSwitchRequestStatus.DONE) {
            setDialogState('submitting', 'planSwitch pending')
        }
    },
    { immediate: true, deep: true }
)

// -----------------------------------------------------------------------------
// Event Handlers
// -----------------------------------------------------------------------------
type PlanChoice = 'free' | 'free_try' | 'early_access' | 'evaluator' | 'student' | 'veille' | 'qms' | 'td' | 'qms_td'

function mapPlanStringToTenantPlan(plan: PlanChoice): TenantPlan {
    switch (plan) {
        case 'free':
            return TenantPlan.FREE
        case 'free_try':
            return TenantPlan.FREE_TRY
        case 'early_access':
            return TenantPlan.EARLY_ACCESS
        case 'evaluator':
            return TenantPlan.EVALUATOR
        case 'student':
            return TenantPlan.STUDENT
        case 'veille':
            return TenantPlan.VEILLE
        case 'qms':
            return TenantPlan.QMS
        case 'td':
            return TenantPlan.TD
        case 'qms_td':
            return TenantPlan.QMS_TD
        default:
            throw new Error(`Unknown plan: ${plan}`)
    }
}

async function handlePlanChoose(payload: { plan: PlanChoice; priceLookupKey?: string }) {
    const selectedPlan = mapPlanStringToTenantPlan(payload.plan)
    await onConfirm(selectedPlan, payload.priceLookupKey)
}

async function onConfirm(selectedPlan: TenantPlan, priceLookupKey?: string) {
    if (shouldContactSalesForPlanSwitch()) {
        visibleLocal.value = false
        openContactSales('upgrade')
        return
    }

    const tenantId = tenantsStore.currentTenant?.id
    if (!tenantId) {
        logger.error('[PlanSwitchDialog] No tenant ID')
        return
    }

    try {
        setDialogState('submitting', 'onConfirm')
        errorMessage.value = null
        await billingStore.startPlanSwitchFlow(tenantId, selectedPlan, priceLookupKey)
    } catch (error) {
        logger.error('[PlanSwitchDialog] Error starting plan switch flow', error)
        errorMessage.value = (error as Error)?.message || t('billing.planSwitch.error')
        setDialogState('error', 'onConfirm catch')
    }
}

function onHide() {
    logger.debug('[PlanSwitchDialog] Dialog hidden, resetting state')
    visibleLocal.value = false
    dialogState.value = 'idle'
    errorMessage.value = null
}

// Load pricing plans when dialog opens
watch(() => props.visible, async (newVal) => {
    if (newVal) {
        logger.debug('[PlanSwitchDialog] Dialog opened, loading pricing plans')
        dialogState.value = 'idle'
        errorMessage.value = null
        await billingStore.loadActivePricingPlans()
    }
})
</script>

<style scoped>
:deep(.plan-switch-transparent) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
}
</style>
